/**
 * QuoteTotalsSection.tsx
 *
 * The Totals band, and the Recalc button.
 *
 * Every figure here comes from the server. Nothing is computed in this file on
 * purpose: legacy had the modal and the quote list each doing their own
 * arithmetic, which is how a quote could show one total on screen and another
 * in the chart list until somebody saved.
 *
 * The two Lock checkboxes hold a coordinator's manual Anesthesia or Operating
 * Room figure against later recalculation. An unlocked side follows the
 * facility's rate scale.
 */
import { useId } from 'react';
import { RefreshCw } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import type { QuoteTotals } from '@/services/quotes.service';
import { QuoteSectionCard } from './QuoteSectionCard';
import { DraftNumberInput } from './DraftNumberInput';

interface Props {
  totals: QuoteTotals | null;
  stale: boolean;
  recalculating: boolean;
  anesthesiaLocked: boolean;
  operatingRoomLocked: boolean;
  costAnesthesia: number;
  costOr: number;
  activeQuote: boolean;
  readOnly: boolean;
  /**
   * Anesthesia and OR are money. Legacy renders these inputs and their Lock
   * checkboxes only for level 3+, or at a facility with nonAdminAllowEdit
   * (encounter-details-ds2.php:1034-1037, 1080-1083).
   */
  pricingReadOnly?: boolean;
  onChange: (patch: {
    anesthesiaLocked?: boolean;
    operatingRoomLocked?: boolean;
    costAnesthesia?: number;
    costOr?: number;
  }) => void;
  onRecalc: () => void;
}

export function QuoteTotalsSection({
  totals, stale, recalculating,
  anesthesiaLocked, operatingRoomLocked, costAnesthesia, costOr,
  activeQuote, readOnly, pricingReadOnly, onChange, onRecalc,
}: Props) {
  const money = 'w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right disabled:bg-slate-100';
  // Both boxes are labelled "Lock", and Checkbox derives its id from the label
  // text, so each needs its own or both labels would point at the first box.
  const lockId = useId();

  return (
    <QuoteSectionCard title="Totals">
      {stale ? (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          These figures are out of date — the last update didn’t go through. Press Recalc to try again.
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 max-md:grid-cols-1">
        <div className="space-y-3">
          <div className="flex items-center gap-3 max-md:flex-wrap">
            <span className="w-32 text-sm text-slate-600">Anesthesia</span>
            <DraftNumberInput
              className={money}
              // A locked side is the coordinator's own number, so it stays
              // editable; an unlocked one is whatever the scale just returned.
              value={anesthesiaLocked ? costAnesthesia : (totals?.anesthesia ?? 0)}
              disabled={readOnly || pricingReadOnly || !anesthesiaLocked}
              onCommit={(value) => onChange({ costAnesthesia: value })}
            />
            <Checkbox
              id={`${lockId}-anesthesia`}
              label="Lock"
              wrapperClassName="items-center gap-1.5"
              aria-label="Lock Anesthesia"
              checked={anesthesiaLocked}
              disabled={readOnly || pricingReadOnly}
              onChange={(e) =>
                onChange({
                  anesthesiaLocked: e.target.checked,
                  // Locking keeps what is on screen right now.
                  ...(e.target.checked ? { costAnesthesia: totals?.anesthesia ?? costAnesthesia } : {}),
                })
              }
            />
          </div>

          <div className="flex items-center gap-3 max-md:flex-wrap">
            <span className="w-32 text-sm text-slate-600">Operating Room</span>
            <DraftNumberInput
              className={money}
              value={operatingRoomLocked ? costOr : (totals?.operatingRoom ?? 0)}
              disabled={readOnly || pricingReadOnly || !operatingRoomLocked}
              onCommit={(value) => onChange({ costOr: value })}
            />
            <Checkbox
              id={`${lockId}-operating-room`}
              label="Lock"
              wrapperClassName="items-center gap-1.5"
              aria-label="Lock Operating Room"
              checked={operatingRoomLocked}
              disabled={readOnly || pricingReadOnly}
              onChange={(e) =>
                onChange({
                  operatingRoomLocked: e.target.checked,
                  ...(e.target.checked ? { costOr: totals?.operatingRoom ?? costOr } : {}),
                })
              }
            />
          </div>

          {!readOnly && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onRecalc}
              loading={recalculating}
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Recalc
            </Button>
          )}
        </div>

        <dl className="space-y-2 text-sm">
          <Figure label="Procedure Discount" value={totals?.procedureDiscount} />
          <Figure label="Other Discounts" value={totals?.otherDiscounts} />
          <Figure label="Total" value={totals?.total} emphasis />
          {/* Legacy shows a balance only on the active quote. */}
          <div className="flex items-center justify-between border-t border-slate-200 pt-2">
            <dt className="font-medium text-slate-700">Balance Due</dt>
            <dd className="font-semibold text-slate-900">
              {activeQuote && totals ? formatCurrency(totals.balanceDue) : ''}
            </dd>
          </div>
        </dl>
      </div>
    </QuoteSectionCard>
  );
}

function Figure({ label, value, emphasis }: { label: string; value: number | undefined; emphasis?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={emphasis ? 'font-medium text-slate-700' : 'text-slate-600'}>{label}</dt>
      <dd className={emphasis ? 'font-semibold text-slate-900' : 'text-slate-700'}>
        {value === undefined ? '—' : formatCurrency(value)}
      </dd>
    </div>
  );
}
